import type { ConnReason, ConnTrack } from './connection.js';
import type { SyncReason, SyncTrack } from './sync.js';

export type IndicatorColour = 'RED' | 'ORANGE' | 'GREEN';
export type IndicatorReason = ConnReason | SyncReason | 'CONN_DWELL' | 'SYNC_DWELL';

export interface IndicatorState {
  colour: IndicatorColour;
  reasons: IndicatorReason[];
}

/** RED iff connection LOST; ORANGE iff INTERMITTENT or sync ≠ GOOD; GREEN otherwise (§7.1). */
export function combineIndicator(conn: ConnTrack, sync: SyncTrack): IndicatorState {
  if (conn.sub === 'LOST') return { colour: 'RED', reasons: [...conn.reasons] };
  const reasons: IndicatorReason[] = [...conn.reasons, ...sync.reasons];
  if (conn.sub === 'INTERMITTENT' && conn.reasons.length === 0) reasons.push('CONN_DWELL');
  if (sync.sub === 'DEGRADED' && sync.reasons.length === 0) reasons.push('SYNC_DWELL');
  if (conn.sub !== 'GOOD' || sync.sub !== 'GOOD') return { colour: 'ORANGE', reasons };
  return { colour: 'GREEN', reasons: [] };
}

import type { MissionState } from '../mission-fsm/types.js';
export type { MissionState };

const ACTIVE_FROM_WEAPON_READY: ReadonlySet<MissionState> = new Set(['WEAPON_READY', 'UAV_READY', 'FIRE_REVIEW', 'IN_FLIGHT', 'AWAITING_DISPOSITION']);
const PRE_WEAPON_READY: ReadonlySet<MissionState> = new Set(['CFF', 'SOLUTION', 'PROGRAMMING', 'LOADING']);

/**
 * Counterpart expected ⇔ role is UAV or FDC, mission non-IDLE, and (state ≥ WEAPON_READY or both
 * seats claimed). Observers, Admin, and a UAV waiting in CFF for an FDC never expect one (§7.1).
 */
export function counterpartExpected(role: 'UAV' | 'FDC' | 'OBSERVER' | 'ADMIN', state: MissionState, seats: { UAV: boolean; FDC: boolean }): boolean {
  if (role !== 'UAV' && role !== 'FDC') return false;
  if (ACTIVE_FROM_WEAPON_READY.has(state)) return true;
  if (PRE_WEAPON_READY.has(state)) return seats.UAV && seats.FDC;
  return false;
}

export interface IndicatorLabel {
  glyph: '●' | '▲' | '⊗';
  label: string;
  /** `±0.1s` style value, or `last m:ss` for RED */
  value: string;
}

function tenths(ms: number): string {
  return `±${(Math.ceil(ms / 100) / 10).toFixed(1)}s`;
}

function mmss(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** The pill text (§7.3). `shownBoundMs` is the pair bound on FDC/UAV when known, else U_self. */
export function describeIndicator(ind: IndicatorState, sync: SyncTrack, shownBoundMs: number | null, offlineForMs: number | null): IndicatorLabel {
  if (ind.colour === 'RED') return { glyph: '⊗', label: 'OFFLINE', value: offlineForMs === null ? '' : `last ${mmss(offlineForMs)}` };
  const value = shownBoundMs === null ? '' : tenths(shownBoundMs);
  if (ind.colour === 'GREEN') return { glyph: '●', label: 'SYNC', value };
  if (sync.sub === 'INVALID') return { glyph: '▲', label: 'RESYNC…', value: '' };
  if (sync.reasons.includes('SYNC_PEER_UNKNOWN')) return { glyph: '▲', label: 'PEER?', value };
  if (ind.reasons.some((r) => r.startsWith('CONN_') && r !== 'CONN_DWELL')) return { glyph: '▲', label: 'INTERMITTENT', value };
  return { glyph: '▲', label: 'SYNC', value };
}
